import React from "react";
import {
  Calendar,
  CalendarX2,
  FileText,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
  Trash2,
  Eye,
  Paperclip,
  Repeat,
} from "lucide-react";
import type { Task, TaskDepartment } from "../../../types/task";

interface TaskCardProps {
  task: Task;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  onViewSubmissions?: (task: Task) => void;
  viewMode?: "list" | "grid";
  canUpdate?: boolean;
  canDelete?: boolean;
  canReview?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onDelete,
  onViewSubmissions,
  viewMode = "list",
  canUpdate = false,
  canDelete = false,
  canReview = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = () => {
    const statusInfo = task.statusInfo;

    if (!statusInfo || !statusInfo.label) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          Bilinmiyor
        </span>
      );
    }

    const label = statusInfo.label;
    const daysText = statusInfo.daysRemaining !== null ? ` (${statusInfo.daysRemaining} Gün)` : "";

    if (label === "Tamamlandı") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm">
          <CheckCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    if (label === "Aktif") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-sm">
          <Clock className="w-3 h-3" />
          {label}
          {daysText}
        </span>
      );
    }

    if (label === "Acil") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
          <Clock className="w-3 h-3 animate-pulse" />
          {label}
          {daysText}
        </span>
      );
    }

    if (label === "Süresi Doldu" || label === "Atanmamış" || label === "Kullanıcı Yok") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    if (label === "Bilinmiyor") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
          <AlertCircle className="w-3 h-3" />
          {label}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-sm">
        <Clock className="w-3 h-3" />
        {label}
        {daysText}
      </span>
    );
  };

  const getItemTypeIcon = () => {
    if (task.itemType === "FILE") {
      return <FileText className="w-4 h-4 text-green-600" />;
    }
    if (task.itemType === "BOTH") {
      return <Paperclip className="w-4 h-4 text-orange-600" />;
    }
    return <MessageSquare className="w-4 h-4 text-blue-600" />;
  };

  const getItemTypeBgColor = () => {
    if (task.itemType === "FILE") {
      return "bg-green-100";
    }
    if (task.itemType === "BOTH") {
      return "bg-orange-100";
    }
    return "bg-blue-100";
  };

  const getFrequencyText = () => {
    const frequencyMap = {
      ONCE: "Bir Kez",
      DAILY: "Günlük",
      WEEKLY: "Haftalık",
      MONTHLY: "Aylık",
    };
    return frequencyMap[task.frequency] || "Bir Kez";
  };

  if (viewMode === "grid") {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div className={`h-10 w-10 ${getItemTypeBgColor()} rounded-full flex items-center justify-center`}>{getItemTypeIcon()}</div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 break-words">{task.title}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-600 font-semibold">{getFrequencyText()}</span>
              </div>
            </div>
          </div>
          {(onEdit || onDelete) && (
            <div className="flex gap-1 ml-2">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                  className="flex items-center justify-center w-7 h-7 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 transition-colors"
                  title="Görevi Düzenle">
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task);
                  }}
                  className="flex items-center justify-center w-7 h-7 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 transition-colors"
                  title="Görevi Sil">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">{task.description}</p>

        <div className="mb-4 space-y-2">
          <div className="text-sm">
            <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
              <Calendar className="h-4 w-4 text-green-600" />
              <span>Başlangıç:</span>
            </div>
            <div className="text-gray-600 ml-6">{formatDate(task.startDate)}</div>
          </div>
          <div className="text-sm">
            <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
              <CalendarX2 className="h-4 w-4 text-red-600" />
              <span>Bitiş:</span>
            </div>
            <div className="text-gray-600 ml-6">{formatDate(task.endDate)}</div>
          </div>
        </div>

        <div className="mb-4">{getStatusBadge()}</div>

        {task.assignedUsers && task.assignedUsers.length > 0 && (
          <div className="mb-4">
            <div className="flex items-start flex-wrap gap-1">
              <span className="text-xs font-medium text-blue-700 mr-2 flex-shrink-0">Atanan Öğrenciler:</span>
              {task.assignedUsers.slice(0, 3).map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 max-w-full min-w-0">
                  <span className="truncate">{`${user.firstName} ${user.lastName}`}</span>
                </span>
              ))}
              {task.assignedUsers.length > 3 && <span className="text-xs text-gray-500">+{task.assignedUsers.length - 3} daha</span>}
            </div>
          </div>
        )}

        {task.departments && task.departments.length > 0 && (
          <div className="mb-4">
            <div className="flex items-start flex-wrap gap-1">
              <span className="text-xs font-medium text-gray-700 mr-2 flex-shrink-0">Departmanlar:</span>
              {task.departments.slice(0, 2).map((dept: TaskDepartment) => (
                <span
                  key={dept.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 max-w-full min-w-0">
                  <span className="truncate">{dept.departmentName}</span>
                </span>
              ))}
              {task.departments.length > 2 && <span className="text-xs text-gray-500">+{task.departments.length - 2} daha</span>}
            </div>
          </div>
        )}

        <div className="mt-auto pt-4 border-t border-gray-100">
          {task.creator && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                  {task.creator.media?.url ? (
                    <img
                      src={task.creator.media.url}
                      alt={`${task.creator.firstName} ${task.creator.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-[10px] font-medium">
                      {(task.creator.firstName || "").charAt(0)}
                      {(task.creator.lastName || "").charAt(0)}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-gray-700 font-medium truncate">
                    {`${task.creator.firstName} ${task.creator.lastName}`.trim() || "Bilinmiyor"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-3">
            <div className="flex flex-col items-start gap-1 text-sm">
              <div className="flex items-center gap-2 w-full">
                <span className="text-gray-600">Tamamlanan:</span>
                <span className="font-semibold text-gray-900">{task.statusInfo?.completedCount || task.submissionCount || 0}</span>
              </div>
              <div className="flex items-center gap-2 w-full">
                <span className="text-gray-600">Toplam:</span>
                <span className="font-semibold text-gray-900">{task.statusInfo?.totalUsers || task.totalTargetCount || 0}</span>
              </div>
            </div>
          </div>
          {canReview && onViewSubmissions && (
            <button
              onClick={() => onViewSubmissions(task)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
              <Eye className="w-4 h-4" />
              Gönderimleri Görüntüle
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3 md:p-4 hover:shadow-md transition-shadow mb-2 min-w-0 overflow-hidden">
      <div className="flex items-start gap-3 min-w-0">
        <div className="flex-shrink-0">
          <div className={`h-8 w-8 ${getItemTypeBgColor()} rounded-full flex items-center justify-center`}>{getItemTypeIcon()}</div>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{task.title}</h3>
          {task.description ? (
            <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2 break-words">{task.description}</p>
          ) : null}

          <div className="mt-3 flex flex-col gap-2 text-xs sm:text-sm text-gray-700 min-w-0">
            <div className="flex flex-col gap-1 text-gray-600 min-w-0">
              <div className="flex items-start gap-2 min-w-0">
                <Calendar className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="break-words">{formatDate(task.startDate)}</span>
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <CalendarX2 className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="break-words">{formatDate(task.endDate)}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-purple-600 flex-shrink-0" />
                <span className="font-medium">{getFrequencyText()}</span>
              </div>

              {task.creator ? (
                <div className="flex items-center gap-2 min-w-0 max-w-full">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {task.creator.media?.url ? (
                      <img
                        src={task.creator.media.url}
                        alt={`${task.creator.firstName} ${task.creator.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-[10px] font-medium">
                        {(task.creator.firstName || "").charAt(0)}
                        {(task.creator.lastName || "").charAt(0)}
                      </span>
                    )}
                  </div>
                  <span className="text-gray-700 font-medium truncate min-w-0">
                    {`${task.creator.firstName} ${task.creator.lastName}`.trim() || "Bilinmiyor"}
                  </span>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-gray-600 whitespace-nowrap">
                  Tamamlanan:{" "}
                  <span className="font-semibold text-gray-900">{task.statusInfo?.completedCount || task.submissionCount || 0}</span>
                </span>
                <span className="text-gray-600 whitespace-nowrap">
                  Toplam:{" "}
                  <span className="font-semibold text-gray-900">{task.statusInfo?.totalUsers || task.totalTargetCount || 0}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end self-stretch flex-shrink-0">
          <div className="flex flex-col items-end gap-2">
            {getStatusBadge()}
          </div>
        </div>
      </div>

      {task.assignedUsers && task.assignedUsers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-start flex-wrap gap-1">
            <span className="text-xs font-medium text-gray-700 mr-2 flex-shrink-0">Atanan Öğrenciler:</span>
            {task.assignedUsers.map((user) => (
              <span
                key={user.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 max-w-full min-w-0">
                <span className="truncate">{`${user.firstName} ${user.lastName}`}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {task.departments && task.departments.length > 0 && (
        <div className={`${task.assignedUsers && task.assignedUsers.length > 0 ? "mt-2" : "mt-3 pt-3 border-t border-gray-100"}`}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 min-w-0 flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-gray-700 flex-shrink-0">Departmanlar:</span>
              {task.departments.map((dept: TaskDepartment) => (
                <span
                  key={dept.id}
                  className="inline-flex items-center h-7 px-2.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 max-w-full min-w-0">
                  <span className="truncate">{dept.departmentName}</span>
                </span>
              ))}
            </div>

            {/* Aksiyonlar (Departmanlar satırı hizasında, sağ altta) */}
            {(canReview && onViewSubmissions) || ((canUpdate || canDelete) && (onEdit || onDelete)) ? (
              <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                {canReview && onViewSubmissions && (
                  <button
                    onClick={() => onViewSubmissions(task)}
                    className="inline-flex items-center justify-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors whitespace-nowrap"
                    title="Gönderimleri Görüntüle">
                    <Eye className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Görüntüle</span>
                  </button>
                )}

                {(canUpdate || canDelete) && (onEdit || onDelete) && (
                  <div className="flex items-center gap-1">
                    {canUpdate && onEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(task);
                        }}
                        className="flex items-center justify-center w-7 h-7 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 transition-colors"
                        title="Görevi Düzenle">
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canDelete && onDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(task);
                        }}
                        className="flex items-center justify-center w-7 h-7 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 transition-colors"
                        title="Görevi Sil">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};